import React from 'react';
import { useFavorites } from '../../../context/FavoritesContext';

const ActionButtons = ({ 
  listing, 
  onAddressClick, 
  onZillowClick, 
  onSummaryClick, 
  onPropertyDetailsClick,
  onResidentDetailsClick,
  isProUser,
  onSubscriptionModal
}) => {
  const { isFavorite, addFavorite, removeFavorite } = useFavorites();
  
  const toggleFavorite = (e) => {
    e.stopPropagation(); // Prevent row expansion
    const listingId = listing.id || listing.property_id;
    
    if (isFavorite(listingId)) {
      removeFavorite(listingId);
    } else {
      addFavorite(listing);
    }
  };

  const handleAddressClick = (e) => {
    e.stopPropagation();
    onAddressClick(e, listing.address, listing.city, listing.state);
  };

  const handleZillowClick = (e) => {
    e.stopPropagation();
    onZillowClick(e, listing.address, listing.city, listing.state);
  };

  const handleSummaryClick = (e) => {
    e.stopPropagation();
    if (isProUser) {
      onSummaryClick(listing);
    } else {
      onSubscriptionModal();
    }
  };

  const handlePropertyDetailsClick = (e) => {
    e.stopPropagation();
    if (isProUser) {
      onPropertyDetailsClick(e, listing);
    } else {
      onSubscriptionModal();
    }
  };

  const handleResidentDetailsClick = (e) => {
    e.stopPropagation();
    if (isProUser) {
      onResidentDetailsClick(listing);
    } else {
      onSubscriptionModal();
    }
  };

  const listingId = listing.id || listing.property_id;
  const isFav = isFavorite(listingId);

  return (
    <div className="flex flex-wrap gap-2 mb-4 pb-4 border-b border-[#d7f5df]">
      {/* View on Map Button - Available to all users */}
      <button 
        onClick={handleAddressClick}
        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        View on Map
      </button>
      
      {/* View on Zillow Button - Available to all users */}
      <button 
        onClick={handleZillowClick}
        className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
        </svg>
        View on Zillow
      </button>
      
      {/* Favorite Button - Available to all users */}
      <button 
        onClick={toggleFavorite}
        className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md ${
          isFav
            ? 'text-white bg-red-500 hover:bg-red-600 focus:ring-red-500'
            : 'text-white bg-pink-500 hover:bg-pink-600 focus:ring-pink-500'
        } focus:outline-none focus:ring-2 focus:ring-offset-2`}
      >
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          className="h-4 w-4 mr-1.5 text-white" 
          fill={isFav ? 'currentColor' : 'none'} 
          viewBox="0 0 24 24" 
          stroke="currentColor"
        >
          <path 
            strokeLinecap="round" 
            strokeLinejoin="round" 
            strokeWidth={isFav ? 0 : 2} 
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" 
          />
        </svg>
        {isFav ? 'Favorited' : 'Add to Favorites'}
      </button>
      
      {/* AI Analysis Button - Pro feature */}
      <button 
        onClick={handleSummaryClick}
        className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white ${
          isProUser 
            ? 'bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800' 
            : 'bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600'
        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        {isProUser ? 'Generate AI Analysis' : 'AI Analysis (Pro)'}
        {!isProUser && (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1 text-yellow-300" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
        )}
      </button>
      
      {/* Resident Details Button - Pro feature */}
      <button 
        onClick={handleResidentDetailsClick}
        className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md text-white ${
          isProUser 
            ? 'bg-gradient-to-r from-green-500 to-green-700 hover:from-green-600 hover:to-green-800' 
            : 'bg-gradient-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600'
        } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
        {isProUser ? 'Show Resident Details' : 'Resident Details (Pro)'}
        {!isProUser && (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1 text-yellow-300" fill="currentColor" viewBox="0 0 24 24">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
          </svg>
        )}
      </button>
    </div>
  );
};

export default ActionButtons;